<script setup>
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ClientBondService from '@/services/ClientBondService';
import BondPurchaseOverlay from '@/components/bonds/BondPurchaseOverlay.vue';
import {formatAmount, formatDate, formatNumber} from '@/helper';
import {useBondsPageGuard} from '@/utils/features';

/**
 * IMA-517 fáze 6: menu Dluhopisy klienta – dluhopisy, které mu lze nabídnout (BE: BondAvailabilityService),
 * a vlastněné dluhopisy, i když už se neprodávají.
 * Fáze 10b: tlačítko Koupit u dluhopisu v prodeji otevře průvodce nákupem (BondPurchaseOverlay).
 */
const {t} = useI18n();
// v instanci bez dluhopisů (B.I.G. Invest SK/CZ) stránka přesměruje na nástěnku
useBondsPageGuard();
const loading = ref(true);
const bonds = ref([]);
// dluhopis, jehož nákup je právě otevřený
const purchaseBond = ref(null);

function loadBonds() {
  return ClientBondService.getAvailable().then(response => {
    bonds.value = response.bonds;
  }).finally(() => {
    loading.value = false;
  });
}

onMounted(loadBonds);

/** Po dokončeném nákupu se seznam načte znovu – změní se vlastněné kusy (štítek „Vlastníte“). */
function onPurchaseClose(purchased) {
  purchaseBond.value = null;
  if (purchased) {
    loadBonds();
  }
}

/**
 * Počet dní do konce prodeje, nebo null, když prodej nekončí brzy (zobrazuje se jen do 30 dnů).
 * Porovnáváme kalendářní dny bez času, aby „končí dnes“ vyšlo 0.
 */
function saleEndsInDays(bond) {
  if (!bond.is_available || !bond.sale_to) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(bond.sale_to + 'T00:00:00') - today) / 86400000);
  return days >= 0 && days <= 30 ? days : null;
}

/** Volný objem v měně dluhopisu (volné kusy × jmenovitá hodnota), null když objem emise neznáme – stejně jako na detailu. */
function freeVolume(bond) {
  if (bond.free_pieces === null || bond.free_pieces === undefined || bond.nominal_value === null) {
    return null;
  }
  return bond.free_pieces * bond.nominal_value;
}

/** Obsazenost emise v % pro ukazatel (upsaný / celkový objem); bez obou objemů se ukazatel nezobrazí. */
function subscribedPct(bond) {
  if (!bond.emission_volume || bond.subscribed_volume === null || bond.subscribed_volume === undefined) {
    return null;
  }
  return Math.min(100, Math.max(0, (bond.subscribed_volume / bond.emission_volume) * 100));
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="page-section__title">{{ t('clientBonds.menu_title') }}</div>
      <div class="page-section__subtitle">{{ t('clientBonds.menu_subtitle') }}</div>
    </div>
    <div class="page-section__content">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border text-primary" role="status"><span class="visually-hidden">{{ t('clientBonds.loading') }}</span></div>
      </div>
      <div v-else-if="!bonds.length" class="card">
        <div class="card-body text-center text-muted">{{ t('clientBonds.no_offer') }}</div>
      </div>
      <div v-else class="row g-3">
        <div v-for="bond of bonds" :key="bond.id" class="col-12 col-md-6 col-xl-4">
          <div class="card h-100">
            <div class="card-header">
              <div class="card-header__title">{{ bond.name }}</div>
              <!-- emitent i ISIN – ISIN je vidět vždy, i když je emitent vyplněný -->
              <div class="card-header__subtitle">{{ [bond.issuer, bond.isin].filter(Boolean).join(' · ') }}</div>
              <div class="d-flex flex-wrap gap-1 mt-2">
                <span v-if="bond.is_available" class="badge bg-success">{{ t('bonds.sale_states.ON_SALE') }}</span>
                <span v-if="bond.owned_quantity" class="badge bg-primary">{{ t('clientBonds.owned_badge', {count: formatNumber(bond.owned_quantity, 0)}) }}</span>
                <span v-if="saleEndsInDays(bond) !== null" class="badge bg-warning text-dark">{{ t('clientBonds.sale_ends_in', {days: saleEndsInDays(bond)}) }}</span>
              </div>
            </div>
            <div class="card-body d-flex flex-column">
              <div class="info-table">
                <div class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.coupon_rate') }}</span>
                  <span class="info-table__value col-6 text-end">{{ bond.coupon_rate === null ? '---' : formatNumber(bond.coupon_rate) + ' % p.a.' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.coupon_frequency') }}</span>
                  <span class="info-table__value col-6 text-end">{{ bond.coupon_frequency_label ?? '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.maturity') }}</span>
                  <span class="info-table__value col-6 text-end">{{ bond.maturity_date ? formatDate(bond.maturity_date, 'd.m.Y') : '---' }}</span>
                </div>
                <div class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.nominal_value') }}</span>
                  <span class="info-table__value col-6 text-end">{{ bond.nominal_value === null ? '---' : formatAmount(bond.nominal_value, bond.currency, 0) }}</span>
                </div>
                <div v-if="bond.is_available && bond.sale_to" class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.sale_to') }}</span>
                  <span class="info-table__value col-6 text-end">{{ formatDate(bond.sale_to, 'd.m.Y') }}</span>
                </div>
                <!-- volný objem s ukazatelem obsazenosti jen u dluhopisů v prodeji (u vlastněných mimo prodej nemá význam) -->
                <div v-if="bond.is_available" class="info-table__row row">
                  <span class="info-table__label col-6">{{ t('clientBonds.free_volume') }}</span>
                  <span class="info-table__value col-6 text-end">{{ freeVolume(bond) === null ? '---' : formatAmount(freeVolume(bond), bond.currency, 0) }}</span>
                  <div v-if="subscribedPct(bond) !== null" class="col-12">
                    <div class="progress mt-1" style="height: 6px;">
                      <div class="progress-bar" role="progressbar" :style="{width: subscribedPct(bond) + '%'}"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-auto pt-3 d-flex gap-2">
                <router-link :to="{name: 'ClientBondDetail', params: {id: bond.id}}" class="btn btn-outline-tenant flex-fill">{{ t('clientBonds.detail') }}</router-link>
                <button v-if="bond.is_available" type="button" class="btn btn-primary flex-fill" @click="purchaseBond = bond">{{ t('bondPurchase.buy') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <BondPurchaseOverlay v-if="purchaseBond" :bond="purchaseBond" @close="onPurchaseClose"/>
  </div>
</template>
